import {
  DEFAULT_DEPLOYMENT_STACK,
  type DeploymentStackConfig,
} from "../lib/deploymentStack";
import type { Workload } from "../lib/types";
import type { AudienceMode } from "../lib/audienceMode";
import {
  buildInfrastructurePlan,
  type InfrastructurePlanSection,
} from "./architecturePlanCopy";
import { fmtInt } from "./costCopy";
import type { Locale } from "./locale";
import { numberLocaleFor } from "./locale";

const DAYS = 30;

function featPlain(w: Workload, locale: Locale): string {
  const uk: Record<string, string> = {
    chat: "спілкування в чаті",
    storage: "збереження профілів і даних",
    search: "пошук по історії",
    ai: "відповіді з допомогою штучного інтелекту",
    realtime: "оновлення в реальному часі",
    files: "завантаження файлів",
  };
  const en: Record<string, string> = {
    chat: "chat-style messaging",
    storage: "saving user profiles and data",
    search: "searching history",
    ai: "AI-generated replies",
    realtime: "live updates",
    files: "file uploads",
  };
  const es: Record<string, string> = {
    chat: "mensajes tipo chat",
    storage: "perfiles y datos guardados",
    search: "búsqueda en el historial",
    ai: "respuestas con IA",
    realtime: "actualizaciones en vivo",
    files: "subida de archivos",
  };
  const m = locale === "uk" ? uk : locale === "es" ? es : en;
  const parts = w.enabledFeatures.map((f) => m[f] ?? f);
  if (parts.length === 0)
    return locale === "uk"
      ? "базовий застосунок"
      : locale === "es"
        ? "app básica"
        : "a basic app";
  return parts.join(locale === "uk" ? ", " : locale === "es" ? ", " : ", ");
}

/**
 * Скорочений план простими словами (режим «не технічний»).
 */
export function buildSimpleInfrastructurePlan(
  locale: Locale,
  w: Workload,
  stack: DeploymentStackConfig = DEFAULT_DEPLOYMENT_STACK,
): InfrastructurePlanSection[] {
  const daily = w.usersDaily * w.requestsPerUser;
  const monthly = daily * DAYS;
  const nf = numberLocaleFor(locale);
  const users = w.usersDaily.toLocaleString(nf);
  const acts = w.requestsPerUser.toLocaleString(nf);
  const aiPct = Math.round(w.aiRequestShare * 100);
  const feats = featPlain(w, locale);

  if (locale === "uk") {
    const sections: InfrastructurePlanSection[] = [
      {
        title: "Що ми маємо на увазі",
        items: [
          "Це орієнтир: «хмара» Amazon дає сервери, бази даних і захист без вашого фізичного обладнання. Цифри не замінюють рахунок від постачальника.",
          "Ми не бачимо ваші знижки, безкоштовні ліміти й реальну поведінку користувачів — тому підсумок показується з широким діапазоном.",
        ],
      },
      {
        title: "Ваш сценарій простими словами",
        items: [
          `Близько ${users} активних користувачів на день, приблизно ${acts} дій на кожного (натискання, запити до застосунку).`,
          `За місяць це порядку ${fmtInt(monthly, locale)} таких звернень.`,
          `У застосунку передбачено: ${feats}.`,
          aiPct > 0
            ? `Приблизно ${aiPct}% звернень можуть залучати «розумні» відповіді (текст від AI) — це зазвичай найдорожча частина, якщо вона є.`
            : "За вашими параметрами частка звернень до AI невелика або нульова — витрати більше про звичайну роботу сервера й бази.",
        ],
      },
      {
        title: "Де зазвичай «тяжіють» гроші",
        items: [
          stack.networkExposure === "private"
            ? "Приватна схема (без прямого доступу з інтернету) додає витрати на захищені канали та мережу — це нормально для корпоративних систем."
            : "Публічний застосунок зазвичай проходить через захищений вхід Amazon; основні витрати — на обробку запитів і зберігання даних.",
          "Якщо у вас увімкнено AI, перевір у блоці витрат, чи не домінує він — тоді варто планувати ліміти та кешування разом із розробниками.",
          "Файли, пошук і чат кожен по-своєму збільшують сховище й навантаження — детальний технічний розклад у режимі «Для фахівців».",
        ],
      },
      {
        title: "Що зробити наступним кроком",
        items: [
          "Порівняй орієнтир із реальним чеком за минулий місяць (поле нижче на сторінці).",
          "Обговоріть із командою, які функції справді потрібні на старті — менше зайвих опцій часто дає прогнозованіші витрати.",
          "Перемкни «Для фахівців», щоб побачити повний технічний план (мережа, сервери, база, безпека).",
        ],
      },
    ];
    return sections;
  }

  if (locale === "es") {
    return [
      {
        title: "Qué significa esto",
        items: [
          "Es una estimación: la nube de Amazon ofrece servidores y bases sin hardware propio. No sustituye tu factura.",
          "No vemos descuentos, free tiers ni el uso real — por eso el total se muestra con un rango amplio.",
        ],
      },
      {
        title: "Tu escenario en lenguaje sencillo",
        items: [
          `Unos ${users} usuarios activos al día y ~${acts} acciones por usuario.`,
          `Eso son del orden de ${fmtInt(monthly, locale)} interacciones al mes.`,
          `Funciones: ${feats}.`,
          aiPct > 0
            ? `Cerca del ${aiPct}% de las peticiones pueden usar IA — suele ser la partida más cara si está activa.`
            : "Poca o ninguna parte del tráfico va a IA en tu configuración.",
        ],
      },
      {
        title: "Dónde suelen ir los costes",
        items: [
          stack.networkExposure === "private"
            ? "Arquitectura privada añade coste de red y acceso seguro — habitual en entornos corporativos."
            : "Una app pública pasa por una entrada segura de Amazon; lo principal suele ser cómputo y almacenamiento.",
          "Si la IA pesa mucho en el desglose, planifica límites y caché con tu equipo técnico.",
          "Para el desglose técnico completo, cambia a «Para expertos».",
        ],
      },
      {
        title: "Próximos pasos",
        items: [
          "Compara con una factura real del mes pasado.",
          "Ajusta funciones con el equipo antes de escalar.",
          "Activa el modo técnico para VPC, Lambda, bases y seguridad en detalle.",
        ],
      },
    ];
  }

  return [
    {
      title: "What this estimate means",
      items: [
        "This is a planning guide: AWS cloud means rented servers, databases, and security without buying hardware. It is not a bill from Amazon.",
        "We do not see your discounts, free tiers, or real user behaviour — that is why the total is shown with a wide range.",
      ],
    },
    {
      title: "Your scenario in plain language",
      items: [
        `Roughly ${users} active people per day, about ${acts} actions each (taps, requests to your product).`,
        `That is on the order of ${fmtInt(monthly, locale)} interactions per month.`,
        `Features in the mix: ${feats}.`,
        aiPct > 0
          ? `About ${aiPct}% of requests may use AI answers — that line is often the biggest cost when it applies.`
          : "Little or none of your traffic uses AI in this setup — spend is mostly “regular” app and storage.",
      ],
    },
    {
      title: "Where money usually goes",
      items: [
        stack.networkExposure === "private"
          ? "A private setup (no direct internet to your app) adds secure networking cost — common for corporate systems."
          : "A public app typically enters through Amazon’s managed front door; most spend is handling requests and storing data.",
        "If AI dominates the breakdown, plan limits and caching with your engineering team.",
        "Files, search, and chat each grow storage and load — switch to “Technical” for the full architecture write-up.",
      ],
    },
    {
      title: "What to do next",
      items: [
        "Compare the guide to a real last-month bill (field on this page).",
        "Trim features with your team before launch — fewer moving parts usually means more predictable spend.",
        "Turn on “Technical” mode to see VPC, Lambda, databases, and security in detail.",
      ],
    },
  ];
}

export function pickInfrastructurePlan(
  locale: Locale,
  w: Workload,
  stack: DeploymentStackConfig,
  audience: AudienceMode,
): InfrastructurePlanSection[] {
  return audience === "simple"
    ? buildSimpleInfrastructurePlan(locale, w, stack)
    : buildInfrastructurePlan(locale, w, stack);
}
